import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, ArrowRight, Upload, MessageSquare } from 'lucide-react'
import { useContextEngineStore } from '@/stores/contextEngineStore'
import { CompletenessRadar } from '@/components/context/CompletenessRadar'
import { DomainCard } from '@/components/context/DomainCard'
import { ALL_DOMAINS } from '@/types/contextEngine'
import type { DomainScore, GapRecommendation } from '@/types/contextEngine'

function GuidedQuest() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center max-w-md mx-auto">
      <h1 className="font-display text-3xl font-bold text-text-primary mb-3">
        This is your startup&apos;s brain.
      </h1>
      <p className="font-display text-xl text-text-secondary mb-8">
        Let&apos;s fill it in.
      </p>

      {/* Empty radar placeholder */}
      <div className="w-64 h-64 rounded-full border border-border/50 flex items-center justify-center mb-8 relative">
        <div className="absolute inset-0 rounded-full bg-accent-amber/5 animate-pulse" />
        <span className="font-mono text-4xl font-bold text-text-tertiary">0</span>
      </div>

      <Link
        to="/context/add"
        className="inline-flex items-center gap-2 px-6 py-3 bg-accent-amber text-bg-deepest font-semibold rounded-lg hover:bg-accent-amber-hover transition-colors"
      >
        <MessageSquare size={18} />
        Start 5-min Interview
      </Link>

      <Link
        to="/documents"
        className="inline-flex items-center gap-1 mt-4 text-sm text-text-secondary hover:text-accent-amber transition-colors"
      >
        or upload your existing docs
        <ArrowRight size={14} />
      </Link>
    </div>
  )
}

function RecommendationCard({ rec }: { rec: GapRecommendation }) {
  const iconMap = {
    start_interview: MessageSquare,
    fill_gap: MessageSquare,
    refresh_domain: ArrowRight,
    upload_document: Upload,
  }
  const Icon = iconMap[rec.type] ?? ArrowRight

  const priorityColors = {
    critical: 'border-accent-error/30 bg-accent-error/5',
    high: 'border-accent-amber/30 bg-accent-amber/5',
    medium: 'border-border',
    low: 'border-border',
  }

  return (
    <Link
      to={rec.actionUrl}
      className={`flex items-start gap-3 p-4 rounded-lg border ${priorityColors[rec.priority]} hover:bg-bg-tertiary/50 transition-colors group`}
    >
      <div className="w-8 h-8 rounded-md bg-bg-tertiary flex items-center justify-center shrink-0 group-hover:bg-accent-amber/10 transition-colors">
        <Icon size={16} className="text-text-tertiary group-hover:text-accent-amber transition-colors" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-text-primary">{rec.title}</div>
        <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">{rec.description}</div>
      </div>
      <span className="text-xs text-accent-amber shrink-0">{rec.actionLabel}</span>
    </Link>
  )
}

export function ContextPage() {
  const { gapAnalysis, gapAnalysisLoading, gapAnalysisError, fetchGapAnalysis } = useContextEngineStore()

  useEffect(() => {
    if (!gapAnalysis && !gapAnalysisLoading && !gapAnalysisError) {
      fetchGapAnalysis()
    }
  }, [gapAnalysis, gapAnalysisLoading, gapAnalysisError, fetchGapAnalysis])

  // Loading
  if (gapAnalysisLoading && !gapAnalysis) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-accent-amber" size={32} />
      </div>
    )
  }

  // Error
  if (gapAnalysisError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-4">
        <p className="text-text-secondary">Couldn&apos;t load your context.</p>
        <button
          onClick={fetchGapAnalysis}
          className="text-sm text-accent-amber hover:underline"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty — guided quest
  if (!gapAnalysis || gapAnalysis.overallScore === 0) {
    return <GuidedQuest />
  }

  // Build domain scores map — fill in zeros for missing domains
  const scoreMap = new Map(gapAnalysis.domainScores.map((s) => [s.domain, s]))
  const allScores: DomainScore[] = ALL_DOMAINS.map((domain) =>
    scoreMap.get(domain) ?? {
      domain,
      score: 0,
      activeChunks: 0,
      avgConfidence: 0,
      oldestChunkDays: 0,
      gaps: [],
    }
  )

  const recommendations = gapAnalysis.recommendations.slice(0, 3)

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto space-y-8">
      {/* Hero: Radar + Score */}
      <section className="flex flex-col lg:flex-row items-center gap-8">
        <CompletenessRadar
          domainScores={allScores}
          overallScore={gapAnalysis.overallScore}
          size={280}
          className="shrink-0"
        />
        <div className="flex-1 text-center lg:text-left">
          <h1 className="font-display text-2xl font-bold text-text-primary mb-2">
            Context Dashboard
          </h1>
          <p className="text-text-secondary">
            Your startup&apos;s knowledge base is{' '}
            <span className="font-mono text-accent-amber font-semibold">
              {Math.round(gapAnalysis.overallScore)}%
            </span>{' '}
            complete across {gapAnalysis.domainScores.filter((d) => d.activeChunks > 0).length} domains.
          </p>
        </div>
      </section>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <section>
          <h2 className="font-display text-lg font-semibold text-text-primary mb-3">
            Recommended Actions
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {recommendations.map((rec) => (
              <RecommendationCard key={rec.id} rec={rec} />
            ))}
          </div>
        </section>
      )}

      {/* Domain Grid */}
      <section>
        <h2 className="font-display text-lg font-semibold text-text-primary mb-3">
          All Domains
        </h2>
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {allScores.map((score) => (
            <DomainCard key={score.domain} score={score} />
          ))}
        </div>
      </section>
    </div>
  )
}
